import type {
  ActualVsBudgetPeriod,
  ActualVsBudgetState,
  CompanyActualVsBudget,
} from '@/features/actual-vs-budget-production/types'

export const comparisonColumnGroups = [
  {
    metric: 'Revenue',
    colSpan: 4,
    columns: ['Actual', 'Budget', '$ variance', '% variance'],
  },
  {
    metric: 'EBITDA',
    colSpan: 4,
    columns: ['Actual', 'Budget', '$ variance', '% variance'],
  },
] as const

export type PortfolioMetric = (typeof comparisonColumnGroups)[number]['metric']

export function selectVisibleComparisonGroups(
  visibleMetrics: readonly PortfolioMetric[]
) {
  return comparisonColumnGroups.filter(({ metric }) =>
    visibleMetrics.includes(metric)
  )
}

export function getPortfolioColumnCount(
  visibleGroups: readonly (typeof comparisonColumnGroups)[number][]
) {
  return 4 + visibleGroups.length * 4
}

export function getComparisonMessage(comparison: {
  state: ActualVsBudgetState
  matchedPeriods: number
  expectedPeriods: number
}): string | null {
  switch (comparison.state) {
    case 'MATCHED':
    case 'ZERO_BUDGET':
      return null
    case 'MISSING_BUDGET':
      return 'Comparison unavailable — Budget data is missing'
    case 'ACTUAL_MISSING':
      return 'Comparison unavailable — Actual data is missing'
    case 'UNMATCHED_PERIOD':
      return 'Comparison unavailable — Actual and Budget periods do not overlap'
    case 'INCOMPLETE_MATCH':
      return `Partial comparison — ${comparison.matchedPeriods} of ${comparison.expectedPeriods} months are available`
  }
}

export type PortfolioSort = 'company-asc' | 'company-desc' | 'latest-close'

export function isPortfolioSort(value: string): value is PortfolioSort {
  return (
    value === 'company-asc' ||
    value === 'company-desc' ||
    value === 'latest-close'
  )
}

export function isActualVsBudgetPeriod(
  value: string
): value is ActualVsBudgetPeriod {
  return value === 'MONTHLY' || value === 'QUARTERLY' || value === 'FYTD'
}

interface PortfolioSelection {
  query: string
  fundId: string
  sort: PortfolioSort
  visibleMetrics: readonly PortfolioMetric[]
}

function getCompanyCompletenessRank(
  company: CompanyActualVsBudget,
  visibleMetrics: readonly PortfolioMetric[]
) {
  let rank = 0
  for (const metric of visibleMetrics) {
    const comparison = company.comparisons.find(
      ({ requestedMetric }) => requestedMetric === metric
    )
    if (!comparison) return 2
    if (
      comparison.state !== 'MATCHED' &&
      comparison.state !== 'ZERO_BUDGET' &&
      comparison.state !== 'INCOMPLETE_MATCH'
    ) {
      return 2
    }
    if (comparison.state === 'INCOMPLETE_MATCH') rank = 1
  }
  return rank
}

export function selectPortfolioCompanies(
  companies: readonly CompanyActualVsBudget[],
  selection: PortfolioSelection
) {
  const normalizedQuery = selection.query.trim().toLocaleLowerCase()
  const selected = companies.filter(
    (company) =>
      (!normalizedQuery ||
        company.companyName.toLocaleLowerCase().includes(normalizedQuery)) &&
      (selection.fundId === 'all' ||
        company.funds.some(({ id }) => id === selection.fundId))
  )

  return [...selected].sort((left, right) => {
    const completenessOrder =
      getCompanyCompletenessRank(left, selection.visibleMetrics) -
      getCompanyCompletenessRank(right, selection.visibleMetrics)
    if (completenessOrder !== 0) return completenessOrder

    if (selection.sort === 'latest-close') {
      const dateOrder = (right.latestClosedActualPeriod ?? '').localeCompare(
        left.latestClosedActualPeriod ?? ''
      )
      if (dateOrder !== 0) return dateOrder
    }

    const nameOrder = left.companyName.localeCompare(right.companyName)
    if (nameOrder !== 0) {
      return selection.sort === 'company-desc' ? -nameOrder : nameOrder
    }

    return left.companyId.localeCompare(right.companyId)
  })
}
